/**
 * Platform Event Types — Config-Driven Registry
 *
 * Single source of truth for all platform event types that can trigger workflows.
 * Shared between frontend (filter builder UI) and backend (event validation + emission).
 *
 * To add a new event type:
 * 1. Add the key to `PlatformEventType`, and an entry here with label, category,
 *    delivery, attributionSource, filterFields, and samplePayload
 * 2. Emit the event from the relevant use case using PlatformEventEmitter
 *
 * Step 1's `delivery` is the one that bites. "Add an entry and emit it" is
 * correct for a REACTIVE event (a fact about the org — broadcast it) and wrong
 * for a PROACTIVE/polled one, whose existence is the verdict of a single
 * trigger's private predicate and which must be `addressed` to that trigger.
 * See PlatformEventDelivery. Emitting an addressed event without a
 * targetTriggerId is dropped and logged, never broadcast — fail closed.
 */

// ── Filter Field Definitions ──────────────────────────────────────────────────

import { DbEnum } from "../../../generated/prisma-enums";

export type FilterFieldType =
  | "agent-select"
  | "deployment-select"
  | "enum-select"
  | "number"
  | "text";

export type FilterFieldOperator = "eq" | "neq" | "in" | "nin" | "gt" | "gte" | "lt" | "lte";

export interface FilterFieldOption {
  value: string;
  label: string;
}

export interface FilterFieldDef {
  key: string;
  label: string;
  fieldType: FilterFieldType;
  /** Static options for enum-select fields */
  options?: FilterFieldOption[];
  /** Dynamic data source for agent-select / deployment-select fields */
  dataSource?: "agents" | "deployments";
  /** Operators available for this field */
  operators: FilterFieldOperator[];
}

// ── Platform Event Type Definition ────────────────────────────────────────────

/**
 * How an event of this type reaches its listeners. Required — every event must
 * answer it, because getting it wrong is silent and expensive (NEX-2435).
 *
 * `broadcast` — REACTIVE events. The event is a FACT ABOUT THE ORGANISATION
 * ("a message arrived", "a call ended"), true independently of any trigger.
 * Whoever is listening and whose filters match should act on it. The emitter
 * does not know or care who those listeners are.
 *
 * `addressed` — PROACTIVE (polled) events. The event is NOT a fact about the
 * org; it is the OUTPUT OF ONE TRIGGER'S PRIVATE PREDICATE. `conversation.idle`
 * does not mean "this chat is idle" — idle is meaningless without a threshold.
 * It means "this chat crossed THE MINTING TRIGGER'S idleConfig". A poller
 * evaluates its own predicate to decide the event exists at all, and nothing
 * downstream re-evaluates it: the dispatcher matches the FilterBuilder only,
 * which knows nothing about thresholds. Broadcasting such an event therefore
 * promotes the minter's threshold into every other listener's config — a
 * trigger polling at 5 minutes fires a workflow configured for 3 hours, and
 * the victim's own config is never consulted. An addressed event is delivered
 * to its minting trigger and to nobody else.
 *
 * The rule of thumb: if you had to read per-trigger config to decide whether
 * to emit, the event is `addressed`. If the event would be equally true with
 * no triggers configured at all, it is `broadcast`.
 */
export type PlatformEventDelivery = "broadcast" | "addressed";

/**
 * Where a workflow run STARTED by an event of this type came from — which row
 * its deployment attribution is inherited from. Required, for the same reason
 * `delivery` is: omitting it would have to default to something, and every
 * default is a silent claim about thousands of runs.
 *
 * Each value names ONE payload field that carries the origin's id, and the id
 * is all that is read. The row it names is then resolved inside the event's
 * own organization, and its stored attribution is what the run inherits:
 *
 * `conversation` — the event happened IN a chat (a message, a status change, a
 * call, an email, a satisfaction score). The run inherits the chat named by
 * `payload.conversation.id`: ATTRIBUTED to the chat's deployment, or
 * NOT_APPLICABLE for an internal chat. A payload that does not carry the id
 * leaves the run UNRESOLVED — never a guess.
 *
 * `workflowExecution` — the event is the outcome of another run. The run
 * inherits that execution's attribution, named by `payload.execution.id`.
 *
 * `none` — the event is about something no deployment originates: agent
 * configuration, knowledge, a customer profile, a workflow publish. The run
 * is NOT_APPLICABLE.
 *
 * A top-level `deploymentId` in a payload is deliberately NOT a source. It is
 * the emitter's snapshot of a claim, typed as data and unchecked against the
 * tenant; the chat or execution row is the fact.
 */
export type PlatformEventAttributionSource = "conversation" | "workflowExecution" | "none";

export interface PlatformEventTypeDef {
  label: string;
  category: string;
  icon: string;
  description: string;
  /**
   * Delivery semantics — see PlatformEventDelivery. Deliberately REQUIRED so a
   * new event type cannot default into broadcast by omission; that default is
   * exactly what made a deleted workflow close a live customer's conversations
   * for weeks. The compiler asks the question; you must answer it.
   */
  delivery: PlatformEventDelivery;
  /**
   * Which row a run started by this event inherits its deployment attribution
   * from — see PlatformEventAttributionSource. Required, and the registry is
   * checked against `PlatformEventType` in both directions, so a new event type
   * does not compile until it answers.
   */
  attributionSource: PlatformEventAttributionSource;
  filterFields: FilterFieldDef[];
  samplePayload: Record<string, unknown>;
  /** When true, the event is defined but not yet emitted from the backend */
  comingSoon?: boolean;
}

// ── Enum Option Constants ─────────────────────────────────────────────────────

/**
 * Chat status filter options — derived from DbEnum so a new Prisma ChatStatus
 * value cannot land without an explicit label update here (the spread forces
 * a compile error). Labels stay human-friendly.
 */
const CHAT_STATUS_LABELS: Record<DbEnum.ChatStatus, string> = {
  OPEN: "Open",
  RUNNING: "Running",
  ARCHIVED: "Archived (closed)",
  DELETED: "Deleted"
};

export const CHAT_STATUS_OPTIONS: FilterFieldOption[] = DbEnum.CHAT_STATUS_VALUES.map((value) => ({
  value,
  label: CHAT_STATUS_LABELS[value]
}));

export const CHAT_TICKET_STATUS_OPTIONS: FilterFieldOption[] = [
  { value: "SUBMITTED", label: "Submitted" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "WAITING_ON_CUSTOMER", label: "Waiting on Customer" },
  { value: "RESOLVED", label: "Resolved" }
];

export const CHAT_RESPONSE_HANDLING_OPTIONS: FilterFieldOption[] = [
  { value: "AUTO", label: "Auto-reply" },
  { value: "ON_APPROVAL", label: "Reply on approval" },
  { value: "MANUAL", label: "Manual reply only" }
];

export const SATISFACTION_FRAMEWORK_OPTIONS: FilterFieldOption[] = [
  { value: "LEGACY_5_STAR", label: "Legacy 5-star" },
  { value: "NPS", label: "NPS (Net Promoter Score)" },
  { value: "CSAT", label: "CSAT (Customer Satisfaction)" },
  { value: "AI_PASSIVE", label: "AI passive analysis" },
  { value: "CUSTOM", label: "Custom" }
];

export const SATISFACTION_SOURCE_OPTIONS: FilterFieldOption[] = [
  { value: "CUSTOMER_PROMPTED", label: "Customer prompted" },
  { value: "AI_PASSIVE", label: "AI passive analysis" },
  { value: "LEGACY", label: "Legacy" }
];

export const DEPLOYMENT_TYPE_OPTIONS: FilterFieldOption[] = [
  { value: "EMBED", label: "Web Chat (Embed)" },
  { value: "API", label: "API" },
  { value: "SLACK", label: "Slack" },
  { value: "TEAMS", label: "Microsoft Teams" },
  { value: "TELEGRAM", label: "Telegram" },
  { value: "FB_MESSENGER", label: "Facebook Messenger" },
  { value: "WHATSAPP", label: "WhatsApp" },
  { value: "TWILIO_SMS", label: "SMS (Twilio)" },
  { value: "TWILIO_VOICE", label: "Voice (Twilio)" },
  { value: "GMAIL", label: "Gmail" },
  { value: "OUTLOOK", label: "Outlook" },
  { value: "IMAP", label: "IMAP Email" },
  { value: "SMTP", label: "SMTP Email" }
];

export const CALL_DIRECTION_OPTIONS: FilterFieldOption[] = [
  { value: "inbound", label: "Inbound" },
  { value: "outbound", label: "Outbound" }
];

export const CUSTOMER_IDENTIFIER_TYPE_OPTIONS: FilterFieldOption[] = [
  { value: "EMAIL", label: "Email" },
  { value: "PHONE", label: "Phone" },
  { value: "USER_ID", label: "User ID" },
  { value: "EXTERNAL_ID", label: "External ID" }
];

export const EMAIL_CHANNEL_OPTIONS: FilterFieldOption[] = [
  { value: "GMAIL", label: "Gmail" },
  { value: "OUTLOOK", label: "Outlook" },
  { value: "IMAP", label: "IMAP" }
];

export const FILE_TYPE_OPTIONS: FilterFieldOption[] = [
  { value: "PDF", label: "PDF" },
  { value: "DOCX", label: "Word Document" },
  { value: "TXT", label: "Text File" },
  { value: "CSV", label: "CSV" },
  { value: "HTML", label: "HTML" },
  { value: "VIDEO", label: "Video" },
  { value: "AUDIO", label: "Audio" },
  { value: "URL", label: "URL/Webpage" }
];

/**
 * Document type filter options for the two events emitted from the `Document`
 * processing pipeline (`knowledge.document_processed`,
 * `document.processing_failed`).
 *
 * 🚨 NOT `FILE_TYPE_OPTIONS`, and the difference is the whole reason this list
 * exists. `FILE_TYPE_OPTIONS` is a hand-written vocabulary belonging to
 * `knowledge.document_uploaded`, whose `fileType` comes from
 * `KnowledgeDocumentService.getKnowledgeTypeFromExtension` — a file-extension
 * map, not a column. The processing events carry `Document.type`, a real
 * `DocumentType` Prisma enum, and the two vocabularies intersect in exactly
 * three values (`PDF`, `CSV`, `AUDIO`). Offering `FILE_TYPE_OPTIONS` on these
 * events would publish five filter values (`DOCX`, `TXT`, `HTML`, `VIDEO`,
 * `URL`) that no emit can ever produce, and hide fourteen that it does —
 * a filter that silently matches nothing, which is the same silent-inertness
 * failure the emitters below exist to remove.
 *
 * Derived from `DbEnum` so a new `DocumentType` member cannot land without an
 * explicit label here: the `Record` is exhaustive, so omitting one is a
 * compile error rather than a value missing from a picker.
 */
const DOCUMENT_TYPE_LABELS: Record<DbEnum.DocumentType, string> = {
  PDF: "PDF",
  CSV: "CSV",
  TEXT: "Text",
  IMAGE: "Image",
  AUDIO: "Audio",
  WEBSITE_FOLDER: "Website",
  WEBSITE_PAGE: "Website page",
  NOTION_PAGE: "Notion page",
  NOTION_DATABASE: "Notion database",
  GOOGLE_DOC: "Google Doc",
  GOOGLE_SHEET: "Google Sheet",
  GOOGLE_DRIVE: "Google Drive file",
  SHAREPOINT: "SharePoint file",
  AIRTABLE_BASE: "Airtable base",
  AIRTABLE_TABLE: "Airtable table",
  FOLDER: "Folder",
  UNKNOWN: "Unknown"
};

export const DOCUMENT_TYPE_OPTIONS: FilterFieldOption[] = DbEnum.DOCUMENT_TYPE_VALUES.map(
  (value) => ({ value, label: DOCUMENT_TYPE_LABELS[value] })
);

// ── Reusable Filter Field Definitions ─────────────────────────────────────────

const CHANNEL_FILTER: FilterFieldDef = {
  key: "channel",
  label: "Channel",
  fieldType: "enum-select",
  options: DEPLOYMENT_TYPE_OPTIONS,
  operators: ["eq", "neq", "in", "nin"]
};

const AGENT_FILTER: FilterFieldDef = {
  key: "agentId",
  label: "Agent",
  fieldType: "agent-select",
  dataSource: "agents",
  operators: ["eq", "neq", "in", "nin"]
};

const DEPLOYMENT_FILTER: FilterFieldDef = {
  key: "deploymentId",
  label: "Deployment",
  fieldType: "deployment-select",
  dataSource: "deployments",
  operators: ["eq", "neq"]
};

// ── Event Type Registry ───────────────────────────────────────────────────────

/**
 * Canonical event-key constant for `conversation.idle`. Imported across the
 * stack (preview hook, panel content, validator, scanner spec) so a key rename
 * is a single-edit refactor instead of a string-literal hunt.
 */
export const IDLE_EVENT_TYPE = "conversation.idle";

/**
 * The registry, checked against `PlatformEventType` in BOTH directions: a union
 * member with no entry and an entry with no union member are each a compile
 * error here, and so is an entry missing a required field. That is what makes
 * `attributionSource` and `delivery` exhaustive over the events that can
 * actually be emitted, rather than over whatever happens to be listed.
 *
 * Exported below under the wider `Record<string, …>` its many string-keyed
 * readers already use.
 */
const PLATFORM_EVENT_TYPE_REGISTRY = {
  // ── Conversations ─────────────────────────────────────────────────────────

  "conversation.message_received": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "New message in inbox",
    category: "Conversations",
    icon: "paper-plane",
    description: "Triggers when a new message is received from a user in an inbox conversation",
    filterFields: [
      CHANNEL_FILTER,
      AGENT_FILTER,
      DEPLOYMENT_FILTER,
      { key: "serviceThreadId", label: "Thread ID", fieldType: "text", operators: ["eq", "neq"] },
      {
        key: "customerIdentityId",
        label: "Customer Identity",
        fieldType: "text",
        operators: ["eq", "neq"]
      }
    ],
    samplePayload: {
      message: {
        id: "msg_123",
        content: "Hello, I need help",
        contentType: "HUMAN",
        createdAt: "2026-04-02T12:00:00.000Z"
      },
      conversation: { id: "chat_456", status: "OPEN", ticketStatus: "SUBMITTED" },
      channel: "EMBED",
      agentId: "agent_789",
      deploymentId: "dep_sample_123",
      serviceThreadId: "+33612345678",
      customerIdentityId: "ci_sample_123",
      channelMetadata: { additionalContext: { profileName: "John Doe" } }
    }
  },

  "conversation.created": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "New conversation",
    category: "Conversations",
    icon: "paper-plane",
    description: "Triggers when a new conversation is initiated by a user",
    filterFields: [
      CHANNEL_FILTER,
      AGENT_FILTER,
      DEPLOYMENT_FILTER,
      { key: "serviceThreadId", label: "Thread ID", fieldType: "text", operators: ["eq", "neq"] },
      {
        key: "customerIdentityId",
        label: "Customer Identity",
        fieldType: "text",
        operators: ["eq", "neq"]
      }
    ],
    samplePayload: {
      conversation: {
        id: "chat_456",
        status: "OPEN",
        ticketStatus: "SUBMITTED",
        createdAt: "2026-04-02T12:00:00.000Z"
      },
      channel: "EMBED",
      agentId: "agent_789",
      deploymentId: "dep_sample_123",
      serviceThreadId: "+33612345678",
      customerIdentityId: "ci_sample_123",
      channelMetadata: { additionalContext: { profileName: "John Doe" } }
    }
  },

  "conversation.status_changed": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Conversation status changed",
    category: "Conversations",
    icon: "arrow-split",
    description:
      "Triggers when a conversation status or ticket status changes (includes reopen, close, escalate)",
    filterFields: [
      CHANNEL_FILTER,
      AGENT_FILTER,
      DEPLOYMENT_FILTER,
      {
        key: "toStatus",
        label: "New status",
        fieldType: "enum-select",
        options: CHAT_STATUS_OPTIONS,
        operators: ["eq", "neq", "in", "nin"]
      },
      {
        key: "fromStatus",
        label: "Previous status",
        fieldType: "enum-select",
        options: CHAT_STATUS_OPTIONS,
        operators: ["eq", "neq", "in", "nin"]
      },
      {
        key: "toTicketStatus",
        label: "New ticket status",
        fieldType: "enum-select",
        options: CHAT_TICKET_STATUS_OPTIONS,
        operators: ["eq", "neq", "in", "nin"]
      },
      {
        key: "fromTicketStatus",
        label: "Previous ticket status",
        fieldType: "enum-select",
        options: CHAT_TICKET_STATUS_OPTIONS,
        operators: ["eq", "neq", "in", "nin"]
      }
    ],
    samplePayload: {
      conversation: { id: "chat_456", status: "ARCHIVED", ticketStatus: "RESOLVED" },
      fromStatus: "OPEN",
      toStatus: "ARCHIVED",
      fromTicketStatus: "IN_PROGRESS",
      toTicketStatus: "RESOLVED",
      channel: "WHATSAPP",
      agentId: "agent_789",
      deploymentId: "dep_sample_123"
    }
  },

  "conversation.response_handling_changed": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Response handling changed",
    category: "Conversations",
    icon: "arrow-split",
    description:
      "Triggers when the response-handling mode of a conversation changes (auto / on-approval / manual). Use to escalate hand-offs, notify supervisors when an operator drops to manual, or pause downstream automations the moment a human takes over.",
    filterFields: [
      CHANNEL_FILTER,
      AGENT_FILTER,
      DEPLOYMENT_FILTER,
      {
        key: "toResponseHandling",
        label: "New mode",
        fieldType: "enum-select",
        options: CHAT_RESPONSE_HANDLING_OPTIONS,
        operators: ["eq", "neq", "in", "nin"]
      },
      {
        key: "fromResponseHandling",
        label: "Previous mode",
        fieldType: "enum-select",
        options: CHAT_RESPONSE_HANDLING_OPTIONS,
        operators: ["eq", "neq", "in", "nin"]
      }
    ],
    samplePayload: {
      conversation: { id: "chat_456", responseHandling: "MANUAL" },
      fromResponseHandling: "AUTO",
      toResponseHandling: "MANUAL",
      channel: "WHATSAPP",
      agentId: "agent_789",
      deploymentId: "dep_sample_123"
    }
  },

  "conversation.assigned": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Conversation assigned",
    category: "Conversations",
    icon: "touch",
    description: "Triggers when a conversation is assigned to a user or agent",
    filterFields: [
      CHANNEL_FILTER,
      AGENT_FILTER,
      DEPLOYMENT_FILTER,
      {
        key: "assignedUserIds",
        label: "Assigned users",
        fieldType: "agent-select",
        dataSource: "agents",
        operators: ["eq", "neq"]
      }
    ],
    samplePayload: {
      conversation: { id: "chat_456", status: "OPEN" },
      assignedUserIds: ["user_abc"],
      agentId: "agent_789",
      channel: "EMBED",
      deploymentId: "dep_sample_123"
    }
  },

  "conversation.unassigned": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Conversation unassigned",
    category: "Conversations",
    icon: "touch",
    description: "Triggers when a conversation is unassigned from a user or agent",
    filterFields: [CHANNEL_FILTER, AGENT_FILTER, DEPLOYMENT_FILTER],
    samplePayload: {
      conversation: { id: "chat_456", status: "OPEN" },
      assignedUserIds: [],
      agentId: "agent_789",
      channel: "EMBED",
      deploymentId: "dep_sample_123"
    }
  },

  "conversation.tagged": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Conversation tagged",
    category: "Conversations",
    icon: "text-indicator",
    description: "Triggers when a conversation topic or tag is updated",
    filterFields: [CHANNEL_FILTER, AGENT_FILTER, DEPLOYMENT_FILTER],
    samplePayload: {
      conversation: { id: "chat_456" },
      topic: "Billing issue",
      previousTopic: null,
      channel: "WHATSAPP",
      agentId: "agent_789",
      deploymentId: "dep_sample_123"
    }
  },

  "conversation.comment_added": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Internal comment added",
    category: "Conversations",
    icon: "scratch-card",
    description: "Triggers when an internal comment is added to a conversation",
    filterFields: [CHANNEL_FILTER, AGENT_FILTER, DEPLOYMENT_FILTER],
    samplePayload: {
      conversation: { id: "chat_456" },
      comment: { id: "comment_123", content: "Escalate to tier 2", authorId: "user_abc" },
      channel: "EMBED",
      agentId: "agent_789",
      deploymentId: "dep_sample_123"
    }
  },

  "conversation.customer_identified": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Customer identified",
    category: "Conversations",
    icon: "search-intelligence",
    description: "Triggers when a customer is identified or linked to a conversation",
    filterFields: [
      CHANNEL_FILTER,
      {
        key: "identifierType",
        label: "Identifier type",
        fieldType: "enum-select",
        options: CUSTOMER_IDENTIFIER_TYPE_OPTIONS,
        operators: ["eq", "neq"]
      }
    ],
    samplePayload: {
      conversation: { id: "chat_456" },
      customer: { id: "cust_123", email: "user@example.com", displayName: "John Doe" },
      identifierType: "EMAIL",
      channel: "EMBED"
    }
  },

  "conversation.thread_inactive": {
    // Polled, but the inbox-inactivity cron dispatches directly and never
    // emits through the bus (it owns per-(rule, chat, period) dedupe), so no
    // event of this type is ever delivered by the listener. Marked broadcast
    // to describe the bus contract it does not currently use; revisit if that
    // cron is ever migrated onto the emitter.
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Conversation went inactive",
    category: "Conversations",
    icon: "zap",
    description:
      "Triggers when an OPEN conversation has had no message activity (inbound, outbound, AI, or operator) for the configured duration. Use to nudge cold leads, auto-resolve abandoned tickets, or escalate when a thread has been dormant. Fires at most once per silence stretch per workflow rule — re-arms automatically the next time any message lands on the thread and a new silence period begins.",
    filterFields: [
      CHANNEL_FILTER,
      AGENT_FILTER,
      DEPLOYMENT_FILTER,
      {
        key: "inactiveDurationMinutes",
        label: "Inactive for at least (minutes)",
        fieldType: "number",
        operators: ["gte", "gt", "eq"]
      },
      {
        key: "ticketStatus",
        label: "Ticket status",
        fieldType: "enum-select",
        options: CHAT_TICKET_STATUS_OPTIONS,
        operators: ["eq", "neq", "in", "nin"]
      }
    ],
    samplePayload: {
      conversation: { id: "chat_456", status: "OPEN", ticketStatus: "WAITING_ON_CUSTOMER" },
      lastActivityAt: "2026-04-02T09:00:00.000Z",
      detectedAt: "2026-04-02T12:00:00.000Z",
      inactiveDurationMinutes: 180,
      ticketStatus: "WAITING_ON_CUSTOMER",
      channel: "WHATSAPP",
      agentId: "agent_789",
      deploymentId: "dep_sample_123"
    }
  },

  "conversation.message_deleted": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Message deleted",
    category: "Conversations",
    icon: "trash",
    description: "Triggers when a message is deleted from a conversation",
    filterFields: [CHANNEL_FILTER, AGENT_FILTER, DEPLOYMENT_FILTER],
    samplePayload: {
      conversation: { id: "chat_456" },
      message: { id: "msg_123", deletedAt: "2026-04-02T12:00:00.000Z" },
      channel: "EMBED",
      agentId: "agent_789",
      deploymentId: "dep_sample_123"
    }
  },

  "satisfaction.score.upserted": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Satisfaction score recorded",
    category: "Conversations",
    icon: "square-check",
    comingSoon: true,
    description:
      "Triggers when a satisfaction score is written for a conversation — by a customer-prompted survey reply, by AI passive analysis of the thread, or by any legacy submission. Use to escalate detractors (NPS <= 6, CSAT <= 2), notify on AI-detected dissatisfaction, or hand high promoters to a referral workflow. Carries the previous score (`prevRawScore`) so threshold-crossing rules — fire only when a thread crosses INTO detractor territory, not on every refresh — are expressible via filter composition.",
    filterFields: [
      CHANNEL_FILTER,
      AGENT_FILTER,
      DEPLOYMENT_FILTER,
      {
        key: "framework",
        label: "Score framework",
        fieldType: "enum-select",
        options: SATISFACTION_FRAMEWORK_OPTIONS,
        operators: ["eq", "neq", "in", "nin"]
      },
      {
        key: "source",
        label: "Source",
        fieldType: "enum-select",
        options: SATISFACTION_SOURCE_OPTIONS,
        operators: ["eq", "neq", "in", "nin"]
      },
      {
        key: "rawScore",
        label: "Score",
        fieldType: "number",
        operators: ["eq", "gt", "gte", "lt", "lte"]
      },
      {
        key: "prevRawScore",
        label: "Previous score",
        fieldType: "number",
        operators: ["eq", "gt", "gte", "lt", "lte"]
      }
    ],
    samplePayload: {
      conversation: { id: "chat_456" },
      score: {
        id: "sat_789",
        framework: "NPS",
        rawScore: 4,
        source: "CUSTOMER_PROMPTED",
        prevRawScore: 8,
        reasoning: null,
        recordedAt: "2026-04-02T12:00:00.000Z"
      },
      framework: "NPS",
      source: "CUSTOMER_PROMPTED",
      rawScore: 4,
      prevRawScore: 8,
      channel: "EMBED",
      agentId: "agent_789",
      deploymentId: "dep_sample_123"
    }
  },

  "conversation.idle": {
    // Polled, not reactive: this event exists only because the idle scanner
    // evaluated THIS trigger's idleConfig (thresholdMinutes + lastMessageBy)
    // against the chat. Nothing downstream re-runs that test, so broadcasting
    // it would hand this trigger's threshold to every other idle listener in
    // the org (NEX-2435). Addressed to its minting trigger, and nobody else.
    delivery: "addressed",
    attributionSource: "conversation",
    label: "Conversation went idle",
    category: "Conversations",
    icon: "clock",
    description:
      "Triggers when an OPEN/RUNNING conversation has had no new message for the configured threshold. Re-fires once a new customer or agent message advances the conversation past the previous fire.",
    // Deployment / Agent / Channel filters reuse the same FilterBuilder
    // alphabet as reactive listeners. Threshold + lastMessageBy + the
    // shared chat-list filters live on a sibling `idleConfig` block
    // (time-based configuration that doesn't fit the field/operator/value
    // shape).
    filterFields: [DEPLOYMENT_FILTER, AGENT_FILTER, CHANNEL_FILTER],
    samplePayload: {
      conversation: {
        id: "chat_456",
        status: "OPEN",
        ticketStatus: "IN_PROGRESS",
        lastMessageUpdatedAt: "2026-04-02T12:00:00.000Z"
      },
      idle: {
        thresholdMinutes: 1200,
        lastMessageBy: "USER",
        firedAt: "2026-04-03T08:00:00.000Z"
      },
      channel: "WHATSAPP",
      agentId: "agent_789",
      deploymentId: "dep_sample_123"
    }
  },

  "satisfaction.submitted": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Satisfaction rating submitted",
    category: "Conversations",
    icon: "square-check",
    description: "Triggers when a customer submits a satisfaction rating (CSAT)",
    filterFields: [
      CHANNEL_FILTER,
      AGENT_FILTER,
      DEPLOYMENT_FILTER,
      {
        key: "satisfaction.score",
        label: "Score",
        fieldType: "number",
        operators: ["eq", "gt", "gte", "lt", "lte"]
      }
    ],
    samplePayload: {
      conversation: { id: "chat_456" },
      satisfaction: {
        score: 4.5,
        comment: "Great support!",
        submittedAt: "2026-04-02T12:00:00.000Z"
      },
      channel: "EMBED",
      agentId: "agent_789",
      deploymentId: "dep_sample_123"
    }
  },

  // ── Calls ─────────────────────────────────────────────────────────────────

  "call.ended": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Phone call ended",
    category: "Calls",
    icon: "zap",
    description:
      "Triggers when a phone call ends. Includes the transcript and, when the call was recorded, " +
      "a recording URL. That URL is a Nexus API endpoint: GET it with your API key; the response " +
      "holds a short-lived download URL.",
    filterFields: [
      AGENT_FILTER,
      DEPLOYMENT_FILTER,
      {
        key: "direction",
        label: "Call direction",
        fieldType: "enum-select",
        options: CALL_DIRECTION_OPTIONS,
        operators: ["eq", "neq"]
      },
      {
        key: "duration",
        label: "Duration (seconds)",
        fieldType: "number",
        operators: ["gt", "gte", "lt", "lte"]
      }
    ],
    samplePayload: {
      call: {
        callSid: "CA_123",
        duration: 180,
        direction: "inbound",
        from: "+1234567890",
        to: "+0987654321"
      },
      // An authenticated endpoint, not a file: GET it with your API key and the
      // response holds a short-lived download URL. Null when nothing was recorded.
      recording: {
        url: "https://api.nexusgpt.io/api/public/v1/calls/CA_123/recording",
        sid: "RE_123"
      },
      transcript:
        "User: Hi, I have a question about order #456.\nAgent: Of course! Let me look that up for you.\nUser: When will it be delivered?\nAgent: Your order is scheduled for delivery on Friday.",
      conversation: { id: "chat_456" },
      agentId: "agent_789",
      direction: "inbound",
      duration: 180,
      deploymentId: "dep_sample_123",
      // Dedicated top-level variable — the AI-generated call summary, kept
      // separate from `transcript` (the dialogue) so downstream nodes can
      // reference {{trigger.sessionNotes}} cleanly. Also mirrored under
      // callMetadata.sessionNotes for backward compatibility.
      sessionNotes: "Customer inquired about order #456. Agent confirmed delivery.",
      callMetadata: {
        from: "+1234567890",
        to: "+0987654321",
        price: 150,
        messages: 12,
        sessionNotes: "Customer inquired about order #456. Agent confirmed delivery."
      }
    }
  },

  "call.started": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Phone call started",
    category: "Calls",
    icon: "zap",
    description: "Triggers when a new phone call is connected",
    filterFields: [
      AGENT_FILTER,
      DEPLOYMENT_FILTER,
      {
        key: "direction",
        label: "Call direction",
        fieldType: "enum-select",
        options: CALL_DIRECTION_OPTIONS,
        operators: ["eq", "neq"]
      }
    ],
    samplePayload: {
      call: { callSid: "CA_123", direction: "inbound", from: "+1234567890", to: "+0987654321" },
      conversation: { id: "chat_456" },
      agentId: "agent_789",
      direction: "inbound",
      deploymentId: "dep_sample_123"
    }
  },

  "call.missed": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Missed call",
    category: "Calls",
    icon: "zap",
    description: "Triggers when a call is not answered or declined",
    comingSoon: true,
    filterFields: [AGENT_FILTER],
    samplePayload: {
      call: { callSid: "CA_123", from: "+1234567890", to: "+0987654321", reason: "no_answer" },
      agentId: "agent_789"
    }
  },

  "call.transferred": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Call transferred",
    category: "Calls",
    icon: "arrow-split",
    comingSoon: true,
    description: "Triggers when a phone call is transferred to another agent or number",
    filterFields: [AGENT_FILTER],
    samplePayload: {
      call: { callSid: "CA_123" },
      transferredFrom: { agentId: "agent_789" },
      transferredTo: { number: "+0987654321" },
      agentId: "agent_789"
    }
  },

  "call.recording_available": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Call recording available",
    category: "Calls",
    icon: "zap",
    description:
      "Triggers when a call recording has been processed and is available for download. " +
      "`recording.url` is a Nexus API endpoint: GET it with your API key; the response holds a " +
      "short-lived download URL.",
    filterFields: [AGENT_FILTER, DEPLOYMENT_FILTER],
    samplePayload: {
      call: { callSid: "CA_123", duration: 180 },
      // An authenticated endpoint, not a file — see the description above.
      recording: {
        url: "https://api.nexusgpt.io/api/public/v1/calls/CA_123/recording",
        sid: "RE_123",
        durationSeconds: 180
      },
      conversation: { id: "chat_456" },
      agentId: "agent_789",
      deploymentId: "dep_sample_123"
    }
  },

  "call.transcript_ready": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Call transcript ready",
    category: "Calls",
    comingSoon: true,
    icon: "text-indicator",
    description: "Triggers when a call transcript has been generated and saved",
    filterFields: [AGENT_FILTER],
    samplePayload: {
      call: { callSid: "CA_123", duration: 180 },
      transcript: "Customer: Hi, I need help. Agent: Of course, how can I assist you?",
      messageIds: ["msg_1", "msg_2", "msg_3"],
      agentId: "agent_789"
    }
  },

  // ── Email ─────────────────────────────────────────────────────────────────

  "email.received": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Email received",
    category: "Email",
    icon: "paper-plane",
    comingSoon: true,
    description: "Triggers when a new email is received via Gmail, Outlook, or IMAP",
    filterFields: [
      {
        key: "channel",
        label: "Email provider",
        fieldType: "enum-select",
        options: EMAIL_CHANNEL_OPTIONS,
        operators: ["eq", "neq"]
      },
      AGENT_FILTER
    ],
    samplePayload: {
      email: {
        from: "customer@example.com",
        to: "support@company.com",
        subject: "Order inquiry",
        snippet: "I have a question about...",
        hasAttachments: false
      },
      conversation: { id: "chat_456" },
      channel: "GMAIL",
      agentId: "agent_789"
    }
  },

  "email.sent": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Email sent",
    category: "Email",
    icon: "paper-plane",
    description: "Triggers when an email response is sent",
    filterFields: [
      {
        key: "channel",
        label: "Email provider",
        fieldType: "enum-select",
        options: EMAIL_CHANNEL_OPTIONS,
        operators: ["eq", "neq"]
      },
      AGENT_FILTER,
      DEPLOYMENT_FILTER
    ],
    samplePayload: {
      email: { to: "customer@example.com", subject: "Re: Order inquiry", threadId: "thread_123" },
      conversation: { id: "chat_456" },
      channel: "GMAIL",
      agentId: "agent_789",
      deploymentId: "dep_sample_123"
    }
  },

  "email.delivery_failed": {
    delivery: "broadcast",
    attributionSource: "conversation",
    label: "Email delivery failed",
    category: "Email",
    icon: "triangle-exclamation",
    description: "Triggers when an email fails to send (quota exceeded, token revoked, etc.)",
    filterFields: [
      {
        key: "channel",
        label: "Email provider",
        fieldType: "enum-select",
        options: EMAIL_CHANNEL_OPTIONS,
        operators: ["eq", "neq"]
      },
      DEPLOYMENT_FILTER,
      { key: "errorType", label: "Error type", fieldType: "text", operators: ["eq", "neq"] }
    ],
    samplePayload: {
      email: { to: "customer@example.com", subject: "Re: Order inquiry" },
      error: { type: "QUOTA_EXCEEDED", message: "Daily sending limit reached" },
      channel: "GMAIL",
      errorType: "QUOTA_EXCEEDED",
      deploymentId: "dep_sample_123"
    }
  },

  // ── Knowledge & Documents ─────────────────────────────────────────────────

  "knowledge.document_uploaded": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Document uploaded",
    category: "Knowledge",
    icon: "page-text-add",
    description: "Triggers when a new document is uploaded to a knowledge base",
    filterFields: [
      {
        key: "fileType",
        label: "File type",
        fieldType: "enum-select",
        options: FILE_TYPE_OPTIONS,
        operators: ["eq", "neq", "in", "nin"]
      },
      { key: "collectionId", label: "Collection", fieldType: "text", operators: ["eq", "neq"] }
    ],
    samplePayload: {
      document: {
        id: "doc_123",
        fileName: "product-guide.pdf",
        fileSize: 1024000,
        fileType: "PDF"
      },
      // Root-level because `filterFields` declares `fileType` unqualified, and
      // `evaluateFilters` resolves a condition's `field` on the payload ROOT.
      // The nested copy is the readable object; this one is the filterable key.
      fileType: "PDF",
      collectionId: "col_456",
      uploadedBy: "user_abc"
    }
  },

  /**
   * Emitted by `DocumentsProcessingService.handleProcessResult` — the single
   * point every ingestion path converges on (file upload, text, website crawl,
   * Google Sheets, Google Drive, Notion, SharePoint), immediately after the row
   * is written `READY`.
   *
   * Not emitted from `KnowledgeDocumentService`, which is where its sibling
   * `knowledge.document_uploaded` lives: the legacy `Knowledge` model is reached
   * only by the two file-upload doors, writes its terminal `DONE` at eleven
   * separate call sites, and carries no processing timestamps at all.
   */
  "knowledge.document_processed": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Document processing completed",
    category: "Knowledge",
    icon: "square-check",
    description: "Triggers when a document has been processed and indexed, ready for AI use",
    filterFields: [
      {
        key: "fileType",
        label: "Document type",
        fieldType: "enum-select",
        options: DOCUMENT_TYPE_OPTIONS,
        operators: ["eq", "neq", "in", "nin"]
      },
      { key: "collectionId", label: "Folder", fieldType: "text", operators: ["eq", "neq"] }
    ],
    samplePayload: {
      document: { id: "doc_123", fileName: "product-guide.pdf", fileType: "PDF", status: "READY" },
      fileType: "PDF",
      collectionId: "fol_456",
      processingTimeMs: 12500
    }
  },

  /**
   * The failure arm of `knowledge.document_processed`, emitted from the same
   * service's `catch` after the row is written `ERROR`. `errorType` is the
   * websocket failure code the same handler already publishes, so a filter on
   * it and the code the console shows are the same value.
   */
  "document.processing_failed": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Document processing failed",
    category: "Knowledge",
    icon: "triangle-exclamation",
    description: "Triggers when a document fails to process or index",
    filterFields: [
      {
        key: "fileType",
        label: "Document type",
        fieldType: "enum-select",
        options: DOCUMENT_TYPE_OPTIONS,
        operators: ["eq", "neq"]
      },
      { key: "errorType", label: "Error type", fieldType: "text", operators: ["eq", "neq"] }
    ],
    samplePayload: {
      document: { id: "doc_123", fileName: "corrupted.pdf", fileType: "PDF", status: "ERROR" },
      error: { type: "PROCESSING_ERROR", message: "Failed to extract text from PDF" },
      fileType: "PDF",
      collectionId: "fol_456",
      errorType: "PROCESSING_ERROR"
    }
  },

  // ── Workflows ─────────────────────────────────────────────────────────────

  "workflow.execution_completed": {
    delivery: "broadcast",
    attributionSource: "workflowExecution",
    label: "Workflow execution completed",
    category: "Workflows",
    icon: "square-check",
    description: "Triggers when a workflow execution finishes successfully",
    filterFields: [
      { key: "workflowId", label: "Workflow", fieldType: "text", operators: ["eq", "neq"] },
      { key: "triggerType", label: "Trigger type", fieldType: "text", operators: ["eq", "neq"] }
    ],
    samplePayload: {
      execution: { id: "exec_123", workflowId: "wf_456", status: "COMPLETED", durationMs: 5200 },
      workflowId: "wf_456",
      workflowName: "Customer Onboarding",
      // A `WorkflowExecutionTrigger` member, verbatim. The emitter used to derive
      // this from the trigger NODE's type (`webhookTrigger` -> `webhook`), which
      // is a property of the graph and therefore identical for every run of the
      // workflow — a builder test run reported `webhook` too. It now reads the
      // execution's own `executionTrigger` column, so a filter on this field
      // selects runs by what actually started them.
      triggerType: "WEBHOOK"
    }
  },

  "workflow.execution_failed": {
    delivery: "broadcast",
    attributionSource: "workflowExecution",
    label: "Workflow execution failed",
    category: "Workflows",
    icon: "triangle-exclamation",
    description: "Triggers when a workflow execution fails",
    filterFields: [
      { key: "workflowId", label: "Workflow", fieldType: "text", operators: ["eq", "neq"] },
      { key: "errorNode", label: "Failed node type", fieldType: "text", operators: ["eq", "neq"] }
    ],
    samplePayload: {
      execution: { id: "exec_123", workflowId: "wf_456", status: "FAILED" },
      error: { nodeId: "node_789", nodeType: "aiTask", message: "Model rate limit exceeded" },
      workflowId: "wf_456",
      workflowName: "Customer Onboarding",
      errorNode: "aiTask"
    }
  },

  "workflow.published": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Workflow published",
    category: "Workflows",
    icon: "square-check",
    description: "Triggers when a workflow is published to production",
    filterFields: [
      { key: "workflowId", label: "Workflow", fieldType: "text", operators: ["eq", "neq"] }
    ],
    samplePayload: {
      workflow: { id: "wf_456", name: "Customer Onboarding", nodeCount: 8 },
      workflowId: "wf_456",
      publishedBy: "user_abc"
    }
  },

  // ── Agents ────────────────────────────────────────────────────────────────

  "agent.created": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Agent created",
    category: "Agents",
    icon: "sparkles-three",
    description: "Triggers when a new AI agent is created",
    filterFields: [],
    samplePayload: {
      agent: { id: "agent_789", firstName: "Support Bot", role: "Customer Support" },
      createdBy: "user_abc"
    }
  },

  "agent.updated": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Agent updated",
    category: "Agents",
    icon: "sparkles-three",
    description: "Triggers when an agent's configuration is updated",
    filterFields: [AGENT_FILTER],
    samplePayload: {
      agent: { id: "agent_789", firstName: "Support Bot" },
      changes: ["instructions", "model", "tools"],
      agentId: "agent_789",
      updatedBy: "user_abc"
    }
  },

  "agent.published": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Agent published",
    category: "Agents",
    icon: "sparkles-three",
    comingSoon: true,
    description: "Triggers when an agent is published or activated",
    filterFields: [AGENT_FILTER],
    samplePayload: {
      agent: { id: "agent_789", firstName: "Support Bot", status: "PUBLISHED" },
      agentId: "agent_789",
      publishedBy: "user_abc"
    }
  },

  // ── Customers (CRM) ──────────────────────────────────────────────────────

  "customer.created": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Customer created",
    category: "Customers",
    icon: "people",
    description: "Triggers when a new customer is identified or imported",
    filterFields: [
      CHANNEL_FILTER,
      {
        key: "identifierType",
        label: "Identifier type",
        fieldType: "enum-select",
        options: CUSTOMER_IDENTIFIER_TYPE_OPTIONS,
        operators: ["eq", "neq"]
      }
    ],
    samplePayload: {
      customer: {
        id: "cust_123",
        displayName: "John Doe",
        email: "john@acme.com",
        phone: "+1234567890"
      },
      channel: "WHATSAPP",
      identifierType: "PHONE"
    }
  },

  "customer.updated": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Customer updated",
    category: "Customers",
    icon: "people",
    description: "Triggers when a customer's profile is updated (name, custom fields, etc.)",
    filterFields: [],
    samplePayload: {
      customer: { id: "cust_123", displayName: "John Doe" },
      changes: ["displayName", "customFields"]
    }
  },

  "customer.tagged": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Customer tagged",
    category: "Customers",
    icon: "text-indicator",
    description: "Triggers when tags are added or removed from a customer",
    filterFields: [
      { key: "tag", label: "Tag", fieldType: "text", operators: ["eq", "neq"] },
      {
        key: "action",
        label: "Action",
        fieldType: "enum-select",
        options: [
          { value: "add", label: "Added" },
          { value: "remove", label: "Removed" }
        ],
        operators: ["eq"]
      }
    ],
    samplePayload: {
      customer: { id: "cust_123", displayName: "John Doe" },
      tags: ["vip"],
      action: "add",
      tag: "vip"
    }
  },

  "customer.merged": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Customers merged",
    category: "Customers",
    icon: "people",
    description: "Triggers when two customer records are merged into one",
    filterFields: [],
    samplePayload: {
      winner: { id: "cust_123", displayName: "John Doe" },
      loser: { id: "cust_456", displayName: "J. Doe" },
      mergedIdentities: 2,
      mergedSessions: 5
    }
  },

  "customer.deleted": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Customer deleted",
    category: "Customers",
    icon: "trash",
    description: "Triggers when a customer is permanently deleted",
    filterFields: [],
    samplePayload: {
      customer: { id: "cust_123", displayName: "John Doe", email: "john@acme.com" },
      deletedBy: "user_abc"
    }
  },

  "customer.note_added": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Customer note added",
    category: "Customers",
    icon: "scratch-card",
    description: "Triggers when an internal note is added to a customer profile",
    filterFields: [],
    samplePayload: {
      customer: { id: "cust_123", displayName: "John Doe" },
      note: { id: "note_123", content: "VIP customer, escalate immediately", authorId: "user_abc" }
    }
  },

  "customer.note_updated": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Customer note updated",
    category: "Customers",
    icon: "scratch-card",
    description: "Triggers when an internal note on a customer profile is edited",
    filterFields: [],
    samplePayload: {
      customer: { id: "cust_123" },
      note: { id: "note_123", content: "Updated note content", authorId: "user_abc" }
    }
  },

  "customer.note_deleted": {
    delivery: "broadcast",
    attributionSource: "none",
    label: "Customer note deleted",
    category: "Customers",
    icon: "scratch-card",
    description: "Triggers when an internal note is removed from a customer profile",
    filterFields: [],
    samplePayload: {
      customer: { id: "cust_123" },
      note: { id: "note_123", deletedBy: "user_abc" }
    }
  }
} satisfies Record<PlatformEventType, PlatformEventTypeDef>;

export const PLATFORM_EVENT_TYPES: Record<string, PlatformEventTypeDef> =
  PLATFORM_EVENT_TYPE_REGISTRY;

// ── Type Helpers ──────────────────────────────────────────────────────────────

export type PlatformEventType =
  | "conversation.message_received"
  | "conversation.created"
  | "conversation.status_changed"
  | "conversation.response_handling_changed"
  | "conversation.assigned"
  | "conversation.unassigned"
  | "conversation.tagged"
  | "conversation.comment_added"
  | "conversation.customer_identified"
  | "conversation.thread_inactive"
  | "conversation.message_deleted"
  | "satisfaction.score.upserted"
  | "conversation.idle"
  | "satisfaction.submitted"
  | "call.ended"
  | "call.started"
  | "call.missed"
  | "call.transferred"
  | "call.recording_available"
  | "call.transcript_ready"
  | "email.received"
  | "email.sent"
  | "email.delivery_failed"
  | "knowledge.document_uploaded"
  | "knowledge.document_processed"
  | "document.processing_failed"
  | "workflow.execution_completed"
  | "workflow.execution_failed"
  | "workflow.published"
  | "agent.created"
  | "agent.updated"
  | "agent.published"
  | "customer.created"
  | "customer.updated"
  | "customer.tagged"
  | "customer.merged"
  | "customer.deleted"
  | "customer.note_added"
  | "customer.note_updated"
  | "customer.note_deleted";

export const PLATFORM_EVENT_TYPE_KEYS = Object.keys(PLATFORM_EVENT_TYPES) as PlatformEventType[];
